import { useEffect } from 'react';
import LoadingSpinner from 'components/loading/LoadingSpinner';
import styled from 'styled-components';
import { FormProvider } from 'react-hook-form';
import parseZone from 'helpers/parseZone';
import useRudderStackTrack from 'hooks/useRudderStackTrack';
import useFormWithErrorTracking from 'hooks/useFormWithErrorTracking';
import { useCreateBookingModificationMutation } from 'components/booking/mutationHooks';
import {
  bookingBoatIdAtom,
  isBookingMultiDayAtom,
  tripFinishDayjsAtom,
  tripStartDayjsAtom,
  bookingTripTimeAtom,
  tripFinishFormattedAtom,
  tripStartFormattedAtom,
  isBookingInitializedAtom,
  selectedBookingPackageAtom,
  bookingDurationAtom,
} from 'components/forms/bookingWidget/jotaiStore';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useBoatCalendar } from 'components/forms/bookingWidget/hooks';
import { Booking } from 'swagger/models';
import useModal from 'components/remodal/v2/useModal';
import { useCloseModal, useSetModalTitle } from 'components/remodal/v2/hooks';
import { useUserDetails } from 'auth/mutations/userHooks';
import useLazySnackbar from 'components/banners/useLazySnackbar';
import Button from 'components/ui/Button/Button';
import ErrorMessage from 'components/ui/ErrorMessage/ErrorMessage';
import Textarea from 'components/ui/Textarea/Textarea';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import { dollarsWithCentsWithSymbol } from '../../helpers/currency';
import { useBreakpoint } from '../BreakpointProvider';
import { mediaMSmMax } from '../../helpers/breakpoints';
import CalendarInput from '../calendar/CalendarInput';
import TimePicker from '../forms/TimePicker/TimePicker';
import ViewSelectedAddonsModal from './ViewSelectedAddonsModal';
import PriceBreakdownModal from './PriceBreakdownModal';
import { apiParamsHourNameMapReverse, createDurationOption } from '../forms/bookingWidget/utils';

const EditBookingModal = ({ booking, onSuccess }: { booking: Booking; onSuccess?: () => void }) => {
  const closeModal = useCloseModal();
  const setModalTitle = useSetModalTitle();

  const { enqueueSnackbar } = useLazySnackbar();
  const diffHours = parseZone(booking?.trip_finish).diff(parseZone(booking?.trip_start), 'hour', true);

  const [bookingBoatId, setBookingBoatId] = useAtom(bookingBoatIdAtom);
  const [tripStart, setTripStart] = useAtom(tripStartDayjsAtom);
  const [tripTime, setTripTime] = useAtom(bookingTripTimeAtom);
  const setBookingDuration = useSetAtom(bookingDurationAtom);
  const setIsBookingMultiDay = useSetAtom(isBookingMultiDayAtom);
  const setTripFinish = useSetAtom(tripFinishDayjsAtom);
  const tripStartFormatted = useAtomValue(tripStartFormattedAtom);
  const tripFinishFormatted = useAtomValue(tripFinishFormattedAtom);
  const setBookingPack = useSetAtom(selectedBookingPackageAtom);
  const setIsBookingInitialized = useSetAtom(isBookingInitializedAtom);
  const rudderTrack = useRudderStackTrack();

  const { userId } = useUserDetails();
  const { boatCalendar, isLoading: isBoatCalendarLoading } = useBoatCalendar({
    startOn: tripStartFormatted,
    endOn: tripFinishFormatted,
    boatId: bookingBoatId,
    packageType: booking?.package_type,
    bookingId: booking.id,
  });

  const createBookingModificationMutation = useCreateBookingModificationMutation({
    onSuccess() {
      if (onSuccess) onSuccess();
      setModalTitle(null);
    },
    onError() {
      enqueueSnackbar('Something went wrong', { variant: 'error' });
    },
  });

  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm || breakpoints.md;

  const isOwner = booking.owner.id === userId || booking?.boat?.boat_manager?.id === userId;
  const isRenter = booking.renter.id === userId;
  const isPending = booking.state === 'pending' || booking.state === 'payment_held';

  // sync form with tripStart & tripTime
  useEffect(() => {
    setValue('tripStart', tripStartFormatted);
    setValue('tripTime', tripTime);
  }, [tripStart, tripTime]);

  useEffect(() => {
    setTripStart(parseZone(booking.trip_start));
    setTripFinish(parseZone(booking.trip_finish));
    setIsBookingMultiDay(booking.trip_type === 'multi_day');
    setBookingPack(booking.package);

    const duration_option = createDurationOption(apiParamsHourNameMapReverse[booking.trip_type], booking.trip_type);
    setBookingDuration(duration_option);

    setTripTime({
      label: parseZone(booking?.trip_start).format('hh:mm A'),
      value: parseZone(booking?.trip_start).format('HH:mm'),
    });
    setBookingBoatId(booking.boat.id);
    setIsBookingInitialized(true);
  }, [booking]);

  // useEffect(() => {
  //   if (isEmpty(boatCalendar)) return;
  //
  //   const dayIndex = dayjs(tripStart).format('YYYY-MM-DD');
  //   if (dayIndex in boatCalendar && !boatCalendar[dayIndex].available) {
  //     setTripStart(tripStart.clone().add(1, 'day'));
  //   }
  // }, [booking.id, tripStart]);

  const methods = useFormWithErrorTracking({
    mode: 'onChange',
    defaultValues: {
      tripStart: null,
      tripTime: null,
      pickup_instructions: booking?.pickup_instructions || '',
      p2p: false,
      terms: false,
      uscg: false,
    },
  });
  const { handleSubmit, register, errors, setValue, watch } = methods;

  const { modal: addonsModal, openModal: openAddonsModal } = useModal(
    <ViewSelectedAddonsModal
      addonList={booking.booking_add_ons}
      totalCents={isOwner ? booking.owner_add_on_total : booking.renter_add_on_total}
    />,
    {
      size: 'medium',
      trackingName: 'view_selected_addons_modal',
    }
  );

  const editBooking = async ({ pickup_instructions }) => {
    if (
      tripStart.isSame(parseZone(booking?.trip_start), 'date') &&
      tripTime.value === parseZone(booking?.trip_start).format('HH:mm')
    ) {
      enqueueSnackbar('Please select a different start date and/or time from the original booking details.', {
        variant: 'error',
        autoHideDuration: 2000,
      });

      return;
    }

    createBookingModificationMutation.mutate({
      id: booking.id,
      body: {
        trip_start: tripStartFormatted,
        trip_start_time: tripTime.value,
        pickup_instructions: pickup_instructions || undefined,
      },
    });
    rudderTrack('booking-action-owner', {
      action: 'modification',
      booking: booking.id,
      location: 'edit-booking-modal',
    });
  };

  const priceBreakdownProps = {
    booking: {
      captain_price: booking.captain_price,
      owner: booking.owner,
    },
    pricing: {
      pre_tax_and_fees_booking_total: booking.pre_tax_and_fees_booking_total,
      special_offer_price: booking.special_offer_price,
      boat_price: booking.boat_price,
      owner_insurance_fee: booking.owner_insurance_fee,
      owner_listing_fee: booking.owner_listing_fee,
      earnings_including_captain: booking.owner_payout,
      owner_add_on_total: booking.owner_add_on_total,
    },
  };

  const { modal: priceBreakdownModal, openModal: openPriceBreakdownModal } = useModal(
    <PriceBreakdownModal {...priceBreakdownProps} />,
    {
      size: 'kindaSmall',
      trackingName: 'price_breakdown_modal',
    }
  );

  const showBreakdown = () => {
    openPriceBreakdownModal();
  };

  const getDescription = () => {
    if (isPending) {
      if (booking.owner.id === userId)
        return `Edit ${booking.renter.first_name}’s booking request. If they approve the edit request, the booking request will be approved. If they decline the edit request, the original details will remain. Please note that your edit request will not block your calendar.`;
      return `Edit your booking request. The updated details will automatically be applied to your request.`;
    }
    if (booking.owner.id === userId)
      return `Send an edit request to ${booking.renter.first_name}. They will have up to 72 hours to approve or decline your request. If they decline your request, the original booking details will remain.`;
    return `Send an edit request to ${booking.owner.first_name}. They will have up to 72 hours to approve or decline your request. If they decline your request, the original booking details will remain.`;
  };

  const getVariantColor = () => {
    if (isRenter) return '#0a4195';
    return '#0e7873';
  };

  const getError = () => {
    const errorsList = [];

    if (errors.p2p) errorsList.push(errors.p2p.message);
    if (errors.terms) errorsList.push(errors.terms.message);
    if (errors.uscg) errorsList.push(errors.uscg.message);

    if (errorsList.length > 0) {
      return `You must agree to the ${errorsList.join(' / ')} to proceed `;
    }

    return null;
  };

  if (isBoatCalendarLoading) {
    return (
      <Wrapper>
        <DummyWidth />
        <SpinnerWrapper>
          <LoadingSpinner color="#72D4BA" />
        </SpinnerWrapper>
      </Wrapper>
    );
  }

  if (createBookingModificationMutation.isSuccess) {
    if (isRenter && isPending) {
      return (
        <Wrapper>
          <SuccessWrapper>
            <DummyWidth />
            <SuccessSVG />
            <SuccessTitle>Booking request has been edited</SuccessTitle>
            <SuccessDesc>Your booking request has been updated</SuccessDesc>
            <Button onClick={closeModal} text="close" variant={isOwner ? 'yellow' : 'renter'} />
          </SuccessWrapper>
        </Wrapper>
      );
    }

    return (
      <Wrapper>
        <SuccessWrapper>
          <DummyWidth />
          <SuccessSVG />
          <SuccessTitle>Edit request has been sent</SuccessTitle>
          <SuccessDesc>{`The ${
            isOwner ? 'renter' : 'owner'
          } will need to confirm your changes before they are finalized. The edit request will expire on ${parseZone(
            createBookingModificationMutation.data?.data?.expires_at
          ).format('MMM D, YYYY • h:mm A')}, and the request will revert back to the original details.`}</SuccessDesc>
          <Button onClick={closeModal} text="close" variant={isOwner ? 'yellow' : 'renter'} />
        </SuccessWrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Heading>{isPending ? 'Edit booking request' : 'Edit booking'}</Heading>
      <DummyWidth />

      <Description>{getDescription()}</Description>

      <Section>
        <SectionHeading>Existing details</SectionHeading>
        <Items>
          <Item>
            <Title>Start date</Title>
            <Value>{parseZone(booking?.trip_start).format('MMM D, YYYY • h:mm A')}</Value>
          </Item>
          <Item>
            <Title>End date</Title>
            <Value>{parseZone(booking?.trip_finish).format('MMM D, YYYY • h:mm A')}</Value>
          </Item>
          <Item>
            <Title>Duration</Title>
            <Value>{diffHours >= 24 ? `${Math.ceil(diffHours / 24)} days` : `${diffHours} hours`}</Value>
          </Item>
          <Item>
            <Title>Captain</Title>
            <Value>{booking?.package_type === 'bareboat' ? 'No captain' : 'Captained'}</Value>
          </Item>
          <Item>
            <Title>Passengers</Title>
            <Value>
              {booking?.passengers} {booking?.passengers > 1 ? 'people' : 'person'}
            </Value>
          </Item>
          {booking.booking_add_ons?.length > 0 && (
            <Item>
              <AddonTitle onClick={openAddonsModal} color={getVariantColor()}>
                Add-ons
              </AddonTitle>
              <Value>{booking.booking_add_ons?.length} selected</Value>
            </Item>
          )}
        </Items>
      </Section>
      <Section>
        <SectionHeading>Edit details</SectionHeading>
        <FormProvider {...methods}>
          <Form onSubmit={handleSubmit(editBooking)}>
            {diffHours < 24 && (
              <InputWrapper>
                <CalendarInput isOwner={isOwner} modalVersion resetTripTime hidePrice hideMultiDay />
                {/* @ts-ignore */}
                <input type="hidden" ref={register('tripStart', { required: true })} name="tripStart" />
                <ErrorMessage error={errors.tripStart} message="Start date is required" />
              </InputWrapper>
            )}

            <InputWrapper>
              <TimePicker isOwner={isOwner} calendar={boatCalendar} useDuration modalVersion editable />
              {/* @ts-ignore */}
              <input type="hidden" ref={register('tripTime', { required: true })} name="tripTime" />
              <ErrorMessage error={errors.tripTime} message="Start time is required" />
            </InputWrapper>

            {isPending && isOwner && (
              <TextAreaInputWrapper>
                <Textarea
                  hasValue={!!watch('pickup_instructions')}
                  inputRef={register({ required: 'Please provide pickup instructions' })}
                  name="pickup_instructions"
                  hasError={!!errors.pickup_instructions}
                  label="Instructions"
                  placeholder="Specify what the renter should expect the day of the booking (e.g. where to meet, where to park and how to access the boat)."
                />
                <ErrorMessage error={errors.pickup_instructions} />
              </TextAreaInputWrapper>
            )}

            <Items>
              {isOwner && (
                <Item>
                  <BoldTitle>Total (excl. fees)</BoldTitle>
                  <Value>{dollarsWithCentsWithSymbol(booking?.pre_tax_and_fees_booking_total || 0)}</Value>
                </Item>
              )}
              {isOwner && (
                <Item>
                  <Title>
                    You earn {!smallScreen && isOwner && <BreakDown onClick={showBreakdown}>Price breakdown</BreakDown>}
                  </Title>
                  <GreenValue>{dollarsWithCentsWithSymbol(booking?.owner_payout)}</GreenValue>
                </Item>
              )}
              {smallScreen && isOwner && (
                <MobileBreakdown onClick={showBreakdown}>
                  <span>Price breakdown</span>
                </MobileBreakdown>
              )}
              {!isOwner && (
                <Item>
                  <BoldTitle>Total (excl. fees)</BoldTitle>
                  <Value>{dollarsWithCentsWithSymbol(booking.pre_tax_and_fees_booking_total)}</Value>
                </Item>
              )}
            </Items>

            {isOwner && isPending && (
              <Agree>
                <Checkboxes>
                  {booking?.insurance_type === 'p2p' && (
                    <Checkbox
                      ownerSecondary
                      inputRef={register({ required: 'Insurance Policy' })}
                      name="p2p"
                      id="p2p"
                      label={
                        <>
                          I have read, understand and agree to the{' '}
                          <a href={booking.insurance_policy_url} target="_blank" rel="noreferrer">
                            Insurance Policy
                          </a>
                        </>
                      }
                    />
                  )}
                  <Checkbox
                    ownerSecondary
                    name="terms"
                    id="terms"
                    inputRef={register({ required: 'Terms of Service and Charter Party Agreement' })}
                    label={
                      <span>
                        I have read, understand and agree to the
                        <a href="/terms" target="_blank">
                          Terms of Service
                        </a>{' '}
                        and{' '}
                        <a href="/legal/time_charter_party_agreement" target="_blank">
                          Charter Party Agreement
                        </a>
                      </span>
                    }
                  />
                  <Checkbox
                    ownerSecondary
                    name="uscg"
                    id="uscg"
                    inputRef={register({ required: 'USCG regulations' })}
                    label={
                      <span>
                        I have read, understand and agree to the
                        <a target="_blank" href="/resources/PaxForHire.pdf">
                          USCG regulations
                        </a>{' '}
                        for this bareboat/captained charter
                      </span>
                    }
                  />
                  {getError() && <ErrorMessage error={{ message: getError() }} />}
                </Checkboxes>
              </Agree>
            )}

            <ButtonsWrapper>
              <Button
                disabled={createBookingModificationMutation.isLoading}
                type="submit"
                text="save edits"
                variant={isOwner ? 'yellow' : 'renter'}
              />
              <Button
                disabled={createBookingModificationMutation.isLoading}
                text="cancel"
                variant={isOwner ? 'owner' : 'renter'}
                outline
                onClick={closeModal}
              />
            </ButtonsWrapper>
          </Form>
        </FormProvider>
      </Section>

      {addonsModal}
      {priceBreakdownModal}
    </Wrapper>
  );
};

const Checkboxes = styled.div`
  & > div {
    padding-top: 12px;
  }

  span {
    color: #5e696a !important;
  }

  label {
    cursor: pointer;
  }

  a {
    color: #0e7873 !important;
    font-size: 14px;
  }
`;

const Agree = styled.div`
  padding-top: 15px;
  font-weight: 500;
  font-size: 14px;
  color: #5e696a !important;
`;

const SuccessWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-flow: column;
  margin: auto;
  min-height: 80vh;
  justify-content: center;
  padding: 25px 0;

  button {
    width: 100%;
  }

  ${mediaMSmMax} {
    min-height: 30vh;
    justify-content: initial;
  }

  svg {
    height: 36px;
  }
`;

const SuccessTitle = styled.div`
  font-weight: 600;
  font-size: 24px;
  padding-bottom: 20px;
  padding-top: 10px;

  ${mediaMSmMax} {
    font-size: 20px;
    padding-bottom: 15px;
  }
`;

const SuccessDesc = styled.div`
  padding-bottom: 50px;
  max-width: 480px;
  text-align: center;

  ${mediaMSmMax} {
    padding-bottom: 30px;
  }
`;

const SuccessSVG = () => (
  <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M30 0C13.4472 0 0 13.4772 0 29.9998C0 46.5526 13.4774 59.9998 30 59.9998C46.5527 59.9998 59.9999 46.5224 59.9999 29.9998C60.0301 13.4471 46.5527 0 30 0ZM30 55.4772C15.9497 55.4772 4.52261 44.0501 4.52261 29.9998C4.52261 15.9496 15.9497 4.52261 30 4.52261C44.0502 4.52261 55.4773 15.9496 55.4773 29.9998C55.5075 44.0501 44.0502 55.4772 30 55.4772ZM43.8693 20.3817C44.7437 21.2863 44.7135 22.7033 43.8392 23.5777L27.3467 39.6481C26.8945 40.0702 26.3517 40.2813 25.7789 40.2813C25.206 40.2813 24.6331 40.0702 24.1809 39.6481L16.2211 31.809C15.3166 30.9346 15.3166 29.4874 16.1909 28.613C17.0653 27.7386 18.5125 27.7085 19.3869 28.5828L25.7789 34.8542L40.7035 20.3216C41.5477 19.4773 42.9949 19.4772 43.8693 20.3817Z"
      fill="#2A8500"
    />
  </svg>
);

const SpinnerWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
`;

const InputWrapper = styled.div`
  padding-bottom: 20px;
`;

const TextAreaInputWrapper = styled(InputWrapper)`
  textarea {
    border-color: rgba(142, 150, 151, 0.5);
  }

  label {
    color: #5e696a;
  }
`;

const DummyWidth = styled.div`
  min-width: 600px;
`;

const BreakDown = styled.span`
  color: #0e7873;
  cursor: pointer;
  font-weight: 500;
  font-size: 12px;
  padding-left: 10px;
`;

const MobileBreakdown = styled(BreakDown)`
  width: 100%;
  display: flex;
  padding-left: 0;

  span {
    //margin-left: auto;
  }
`;

const Form = styled.form`
  [class*='ErrorMessage'] {
    margin-bottom: 0;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  padding-top: 25px;
  flex-flow: column;
  gap: 20px;
`;

const Section = styled.div`
  border-top: 1px solid #dbdfe5;
  padding-top: 25px;
  margin-top: 25px;

  textarea {
    resize: none;
    min-height: 105px;
  }
`;

const SectionHeading = styled.div`
  font-weight: bold;
  display: flex;
  padding-bottom: 20px;
  font-size: 16px;
`;

const Items = styled.div``;

const Item = styled.div`
  display: flex;
  padding-bottom: 12px;
`;

const Title = styled.div`
  font-weight: 500;
`;

const AddonTitle = styled(Title)<{ color: string }>`
  color: ${({ color }) => color};
  cursor: pointer;
`;

const BoldTitle = styled(Title)`
  font-weight: 600;
`;

const Value = styled.div`
  justify-self: flex-end;
  margin-left: auto;
  font-weight: 600;
`;

const GreenValue = styled(Value)`
  color: #2a8500;
  font-weight: 700;
`;

const Wrapper = styled.div`
  max-width: 600px;
  ${mediaMSmMax} {
    font-size: 14px;
  }
`;

const Heading = styled.h1`
  font-weight: bold;
  font-size: 24px;
  margin-bottom: 10px;

  ${mediaMSmMax} {
    display: none;
  }
`;

const Description = styled.div`
  max-width: fit-content;
`;

export default EditBookingModal;
